import React, { useMemo, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import StikiBoldIcon from './images/bold-24.png'
import StikiBoldActiveIcon from './images/bold-active-24.png'
import StikiItalicIcon from './images/italic-24.png'
import StikiItalicActiveIcon from './images/italic-active-24.png'
import StikiColorIcon from './images/color-fan-24.png'
import StikiColorActiveIcon from './images/color-fan-48.png'
import StikiResizeIcon from './images/resize-24.png'
import StikiCloseIcon from './images/close-cross-24.png'

import StikiContent from './StikiContent'

import {
  updateStikiAsync,
  updateDirtyStikisAsync,
  destroyStikiAsync,
  setStikiSelected,
  setStikiUnselected,
  setStikiDirty,
  setStikisColor,
  selectStiki,
  selectCurrentStikiId,
  selectAllSelectedStikisOnPage,
} from './stikisSlice'
import {
  startStikisMove,
  startResize,
  selectResizeOffset,
  setUIColor,
  selectUIColorCurrent,
  selectMarqueeIsSelecting,
} from '../ui/uiSlice'

import { selectPageIsMine } from '../pages/pagesSlice'
import { calculateColor } from '../../helpers'
import {
  STIKI_HALO_BORDER,
  STIKI_MIN_WIDTH,
  STIKI_MIN_HEIGHT,
} from '../../constants'

const classes = {
  resizeButtonContainer: {
    // .resizeButtonContainer {
    position: 'absolute',
    left: '100%',
    top: '100%',
  },
  stikiButton: {
    // .stikiButton
    position: 'absolute',
    width: 24,
    minWidth: 24,
    height: 24,
    minHeight: 24,
    cursor: 'pointer',
    border: 0,
    backgroundColor: 'transparent',
    '& .curveL': {
      // .stikiButton.curveL {
      borderRadiusTopLeft: 12,
      borderRadiusBottomLeft: 12,
    },
    '& .curveR': {
      // .stikiButton.curveR {
      borderRadiusTopRight: 12,
      borderRadiusBottomRight: 12,
    },
    '&.close': {
      // .stikiButton.close {
      left: -7,
      top: -17,
      // backgroundImage: 'image-url("close-cross-24.png")',
      backgroundImage: `url(${StikiCloseIcon})`,
    },
    '&.bold': {
      // .stikiButton.bold {
      left: 7,
      top: -17,
      // backgroundImage: { BoldImage },
      backgroundImage: `url(${StikiBoldIcon})`,
    },
    '&.bold.active': {
      // .stikiButton.bold.active {
      // backgroundImage: 'image-url("bold-active-24.png")',
      backgroundImage: `url(${StikiBoldActiveIcon})`,
    },
    '&.italic': {
      // .stikiButton.italic {
      left: 31,
      top: -17,
      backgroundImage: `url(${StikiItalicIcon})`,
    },
    '&.italic.active': {
      // .stikiButton.italic.active {
      backgroundImage: `url(${StikiItalicActiveIcon})`,
    },
    '&.color': {
      // .stikiButton.color {
      left: -17,
      top: -17,
      // backgroundImage: 'image-url("color-fan-24.png")',
      backgroundImage: `url(${StikiColorIcon})`,
    },
    '&.color.active': {
      // .stikiButton.color.active {
      top: -41,
      left: -41,
      width: 48,
      height: 48,
      // backgroundImage: 'image-url("color-fan-48.png")',
      backgroundImage: `url(${StikiColorActiveIcon})`,
      borderRadiusTopLeft: 16,
    },
    '&.resize': {
      // .stikiButton.resize {
      left: -8,
      top: -8,
      cursor: 'se-resize',
      // backgroundImage: 'image-url("resize-24.png")',
      backgroundImage: `url(${StikiResizeIcon})`,
    },
  },
  closeButtonContainer: {
    // .closeButtonContainer {
    position: 'absolute',
    left: '100%',
    top: 0,
  },
  colorButtonContainer: {
    // .colorButtonContainer {
    left: 0,
    position: 'absolute',
    top: 0,
  },
}

export default function StikiButtons({ stikiId }) {
  const stiki = useSelector(selectStiki(stikiId))
  const isMine = useSelector(selectPageIsMine(stiki?.page_id))
  const currentStikiId = useSelector(selectCurrentStikiId)

  const pageId = stiki.page_id
  const selectedStikisSelector = useMemo(
    () => selectAllSelectedStikisOnPage(pageId),
    [pageId]
  )
  const selectedStikiIds = useSelector(selectedStikisSelector)

  ///////////////////////////////////////////////
  //
  // Hooks
  //

  const dispatch = useDispatch()

  ///////////////////////////////////////////////
  //
  // Local state
  //

  const [isColoring, setIsColoring] = useState(false)
  const [hasColored, setHasColored] = useState(false)
  const [newColor, setNewColor] = useState(stiki.color)

  ///////////////////////////////////////////////
  //
  // Resize button event handlers
  //

  const handleResizeMouseDown = (e) => {
    // console.log('<Stiki/>.handleResizeMouseDown()')

    dispatch(
      startResize({
        x: e.pageX,
        y: e.pageY,
      })
    )

    e.preventDefault()
    e.stopPropagation()
  }

  ///////////////////////////////////////////////
  //
  // Color button event handlers
  //

  const handleColorMouseDown = (e) => {
    // console.log('<StikiButtons />.handleColorMouseDown()')
    if (!isColoring) {
      setIsColoring(true)
    } else {
      setHasColored(true)
    }
    e.preventDefault()
    e.stopPropagation()
  }

  const handleColorMouseMove = (e) => {
    // ('<StikiButtons />.handleColorMouseMove()')
    if (isColoring) {
      setHasColored(true)

      const { top, right, bottom, left } = e.target.getBoundingClientRect()
      const x = e.pageX - left
      const y = e.pageY - top
      const color = calculateColor(x, y)

      if (Boolean(color)) {
        setNewColor(color)
        dispatch(setUIColor({ current: color, hasChanged: true }))
      } else {
        setNewColor(stiki.color)
        dispatch(setUIColor({ current: stiki.color, hasChanged: false }))
      }
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleColorMouseUp = (e) => {
    console.log('<StikiButtons />.handleColorMouseUp()')
    if (hasColored) {
      e.stopPropagation()
      e.preventDefault()
      dispatch(setStikisColor({ stikiIds: selectedStikiIds, color: newColor }))
      // The pick is over, so the preview is too. Left set, it went on painting
      // every selected stiki in this colour, including ones selected later.
      dispatch(setUIColor({ current: newColor, hasChanged: false }))
      dispatch(updateDirtyStikisAsync(pageId))
      setNewColor(stiki.color)
      setIsColoring(false)
      setHasColored(false)
    } else {
      console.log(`  hasColored = ${hasColored}`)
      e.stopPropagation()
      e.preventDefault()
      // dispatch(setStikiColor({ stikiId: stikiId, color: newColor }))
      // dispatch(setStikiDirty(stikiId))
      // dispatch(updateStikiAsync(stikiId))
      // setNewColor(stiki.color)
      setHasColored(true)
    }
  }

  const handleColorClick = (e) => {
    console.log('<StikiButtons />.handleColorClick()')
  }

  ///////////////////////////////////////////////
  //
  // Destroy button event handlers
  //

  const handleAndStopPropagation = (e) => {
    // console.log('<StikiButtons />.handleAndStopPropagation()')
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDestroyClicked = (e) => {
    console.log(`<StikiButtons />.handleDestroyClicked() stikiId:${stikiId}`)
    dispatch(destroyStikiAsync(stikiId))
    e.preventDefault()
    e.stopPropagation()
  }

  ///////////////////////////////////////////////
  //
  // Formatting button event handlers
  //

  const handleBoldMouseDown = (e) => {
    // console.log('<StikiButtons />.handleBoldMouseDown()')
    document.execCommand('bold', false)
    e.preventDefault()
    e.stopPropagation()
  }

  const handleItalicMouseDown = (e) => {
    // console.log('<StikiButtons />.handleItalicMouseDown()')
    document.execCommand('italic', false)
    e.preventDefault()
    e.stopPropagation()
  }

  ///////////////////////////////////////////////
  //
  // Markup
  //

  // Every one of these edits the stiki — resize, delete, colour, bold, italic —
  // so a page someone shared with me shows none of them.
  if (!isMine) {
    return null
  }

  return (
    <React.Fragment>
      <Box sx={classes.resizeButtonContainer}>
        <Box
          sx={classes.stikiButton}
          className={clsx('resize', 'curveL', 'curveR')}
          title='Resize'
          onMouseDown={handleResizeMouseDown}
        ></Box>
      </Box>
      <Box sx={classes.closeButtonContainer}>
        <Box
          sx={classes.stikiButton}
          className={clsx('close', 'curveL', 'curveR')}
          title='Delete'
          onClick={handleDestroyClicked}
        ></Box>
      </Box>
      <Box sx={classes.colorButtonContainer}>
        <Box
          sx={classes.stikiButton}
          className={clsx('color', 'curveL', {
            active: isColoring,
          })}
          title='Color'
          onMouseDown={handleColorMouseDown}
          onMouseMove={handleColorMouseMove}
          onMouseUp={handleColorMouseUp}
          // onClick={handleColorClick}
        ></Box>
      </Box>
      <Button
        sx={classes.stikiButton}
        className={clsx('bold')}
        title='Bold'
        onMouseDown={handleBoldMouseDown}
        onMouseMove={handleAndStopPropagation}
        onMouseUp={handleAndStopPropagation}
        onClick={handleAndStopPropagation}
      ></Button>
      <Button
        sx={classes.stikiButton}
        className={clsx('italic', 'curveR')}
        title='Italic'
        onMouseDown={handleItalicMouseDown}
        onMouseMove={handleAndStopPropagation}
        onMouseUp={handleAndStopPropagation}
        onClick={handleAndStopPropagation}
      ></Button>
    </React.Fragment>
  )
}
